/**
 * Un número tecleado, a lo que entiende el servidor: «1234.56».
 *
 * ⚠ **La coma y el punto valen los dos como separador decimal.** Aquí se escribe
 * «25,50», pero el teclado numérico de medio mundo pone un punto, y lo que se copia
 * de un correo o de la web del banco llega con cualquiera de los dos. Rechazar uno
 * obligaba a borrar y reescribir la cifra, que es justo cuando se equivoca un dígito.
 *
 * Las reglas, para que no haya adivinanzas:
 *
 * - **Un solo separador, una sola vez: es el decimal.** «25,50» y «25.50» son lo
 *   mismo. «1.234» es uno coma dos tres cuatro, NO mil doscientos: para dinero, con
 *   dos decimales, eso lo rechaza quien valida el importe en vez de convertirlo en
 *   mil en silencio.
 * - **Los dos a la vez: el ÚLTIMO es el decimal** y el otro separa los miles.
 *   «1.234,56» y «1,234.56» son lo mismo.
 * - **El mismo repetido: separa miles.** «1.234.567» es un millón y pico.
 * - **Los miles tienen que ser de tres cifras.** «12.34,56» no es un número: leerlo
 *   como 1234,56 sería inventarse lo que se quiso escribir.
 *
 * Devuelve `null` si no es un número positivo; sin signo, porque ningún campo que
 * pase por aquí admite un negativo.
 */
export function normalizarDecimal(texto: string): string | null {
  const limpio = texto.replace(/[\s ]/g, "")
  if (limpio === "") return null
  const punto = limpio.lastIndexOf(".")
  const coma = limpio.lastIndexOf(",")

  let entera: string
  let fraccion: string | null
  if (punto >= 0 && coma >= 0) {
    const decimal = punto > coma ? "." : ","
    const miles = decimal === "." ? "," : "."
    const [antes = "", despues = "", ...sobra] = limpio.split(decimal)
    if (sobra.length > 0) return null
    const grupos = antes.split(miles)
    if (!milesBienAgrupados(grupos)) return null
    entera = grupos.join("")
    fraccion = despues
  } else if (punto >= 0 || coma >= 0) {
    const separador = punto >= 0 ? "." : ","
    const partes = limpio.split(separador)
    if (partes.length === 2) {
      entera = partes[0] ?? ""
      fraccion = partes[1] ?? ""
    } else {
      if (!milesBienAgrupados(partes)) return null
      entera = partes.join("")
      fraccion = null
    }
  } else {
    entera = limpio
    fraccion = null
  }

  if (!/^\d+$/.test(entera)) return null
  if (fraccion === null) return entera
  if (!/^\d+$/.test(fraccion)) return null
  return `${entera}.${fraccion}`
}

/** «1», «234», «567»: el primero de una a tres cifras, los demás de tres exactas. */
function milesBienAgrupados(grupos: string[]): boolean {
  const [primero = "", ...resto] = grupos
  return /^\d{1,3}$/.test(primero) && resto.every((g) => /^\d{3}$/.test(g))
}

/**
 * Lo que se manda al servidor desde un campo numérico: normalizado si se entiende, y
 * tal cual si no — así la respuesta 422 del servidor dice qué está mal, en vez de
 * mandar un vacío que se leería como «no lo toques».
 */
export function paraElServidor(texto: string): string {
  return normalizarDecimal(texto) ?? texto.trim()
}

/** Lo mismo para un campo opcional: vacío sigue siendo `null`. */
export function decimalOpcional(texto: string | null): string | null {
  return texto === null ? null : paraElServidor(texto)
}
